/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  message,
  Input,
  Button,
  Result as AntResult,
  Tabs,
  DatePicker,
  Typography,
  Tooltip,
  Modal,
} from 'antd';
import styled from 'styled-components';
import CustomTable from 'components/Table';
import { onError } from 'utils/handlers';
import { fullName } from 'api/cuentaunicasir';
import NumericInput from 'components/NumericInput';
import {
  getDepositosEnGarantia,
  patchDepositoEnGarantia,
  postCancelarDepositoEnGarantia,
  postDepositoEnGarantia,
  postGenerarCargoDeDeposito,
} from 'api/recaudacion/depositosEnGarantia';
import { useHistory } from 'react-router-dom';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import FormSubmitControls from 'components/FormSubmitControls';
import { getEstatusDeDepositoEnGarantia } from 'api/recaudacion';
import Select from 'components/Select';
import { getUsuario } from 'api/usuarios/usuarios';
import currencyFormatter from 'utils/toCurrency';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { getRecibosPDF, baseURI, getTickets } from 'api/recaudacion/recibos';
import Previewer from 'components/Previewer';
import { getTipoDeCargo } from 'api/recaudacion/tiposDeCargo';
import { getModelPermission } from 'utils/permission';
import MovimientosDeDepositos, { permissionConfig as permissionConfigMovimientos } from './MovimientosDeDepositos';

const { TabPane } = Tabs;

export const permissionConfig = {
  permissionModel: 'ingresodepositoengarantia',
  catalogsModels: [
    'moneda',
    'tipodecargo',
    'estatusdeldeposito',
    'usuario',
    'recibo',
    'configuracionentidad',
  ],
  tabsModels: [permissionConfigMovimientos],
};

// eslint-disable-next-line react/prop-types
const DepositosEnGarantia = ({ registro_de_beneficiario }) => {
  const permission = useMemo(
    () => getModelPermission(
      permissionConfig.permissionModel,
      permissionConfig.catalogsModels,
      permissionConfig.tabsModels,
    ),
    [],
  );
  const history = useHistory();
  const moneda = useSelector(({ auth }) => auth.entidad.moneda);
  const _entidad = useSelector(({ auth }) => auth.entidad);
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [formCancel] = Form.useForm();
  const [formRecibo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [mutable, setMutable] = useState(true);

  const [estatus, setEstatus] = useState([]);
  const [emisor, setEmisor] = useState([]);

  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [base64, setBase64] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [current, setCurrent] = useState(null);

  const [visibleCancel, setVisibleCancel] = useState(false);
  const [tipoDeCargo, setTipoDeCargo] = useState(null);

  const onCancel = () => {
    setVisible(false);
    setCurrent(null);
    form.resetFields();
    formRecibo.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setMutable(true);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDepositosEnGarantia({ registro_de_beneficiario });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_estatus, _data] = await Promise.all([
        getEstatusDeDepositoEnGarantia(),
        getDepositosEnGarantia({ registro_de_beneficiario }),
      ]);
      if (_entidad?.tipo_de_cargo) {
        const _tipoDeCargo = await getTipoDeCargo(_entidad?.tipo_de_cargo);
        if (mounted) {
          setTipoDeCargo(_tipoDeCargo);
        }
      }
      if (mounted) {
        setEstatus(_estatus);
        setEmisor([user]);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [registro_de_beneficiario]);

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    importe: [
      requiredRule,
      {
        validator: async () => {
          const val = form.getFieldValue('importe_de_deposito');
          if (val < tipoDeCargo?.importe_minimo) {
            throw new Error(`El importe mínimo es de ${currencyFormatter(tipoDeCargo?.importe_minimo)}`);
          } else if (val > tipoDeCargo?.importe_maximo) {
            throw new Error(`El importe máximo es de ${currencyFormatter(tipoDeCargo?.importe_maximo)}`);
          }
        },
      },
    ],
  };

  const columns = [
    {
      titleText: 'Usuario Que Emitió',
      dataIndex: 'usuario_que_emitio',
      key: 'usuario_que_emitio',
      width: 300,
    },
    {
      titleText: 'Folio de depósito',
      dataIndex: 'folio_de_recibo_de_ingreso',
      key: 'folio_de_recibo_de_ingreso',
      width: 300,
    },
    {
      titleText: 'Fecha de Emisión',
      dataIndex: 'fecha_emision_de_recibo',
      key: 'fecha_emision_de_recibo',
      width: 300,
      render: (text) => text?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe_de_deposito',
      key: 'importe_de_deposito',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
      align: 'right',
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_de_deposito',
      key: 'estatus_de_deposito',
      width: 200,
      render: (val) => estatus.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones_de_deposito',
      key: 'observaciones_de_deposito',
      width: 400,
    },
  ];

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setCurrent(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const generarCargo = async (id) => {
    if (id && !selectedRowKeys.length) {
      const created = await postGenerarCargoDeDeposito(id);
      if (created) {
        message.info('Cargo creado por depósito en garantía.');
      }
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.registro_de_beneficiario = registro_de_beneficiario;
      const saved = await (!key ? postDepositoEnGarantia(values)
        : patchDepositoEnGarantia(key, values));
      if (saved) {
        await generarCargo(saved.id);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickAdd = () => {
    onCancel();
    form.setFieldsValue({
      usuario_que_emitio: `${user?.first_name || ''} ${user?.last_name
        || ''} ${user?.second_last_name || ''}`,
      usuario_emitio: user.id,
    });
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (match.usuario_emitio !== user.id) {
      const _emisor = await getUsuario(match.usuario_emitio);
      setEmisor([{ ..._emisor, nombre_completo: fullName(_emisor, false) }]);
    }
    setVisible(true);
    form.setFieldsValue(match);
    formRecibo.setFieldsValue(match);
    setMutable(match.estatus_de_deposito !== 3);
    setLoading(false);
  };

  const onClickPreview = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    const results = await getRecibosPDF([match.recibo_de_ingreso]);
    if (results.success) {
      setBase64(results.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const onClickTicket = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    const results = await getTickets([match.recibo_de_ingreso]);
    if (results.success) {
      setBase64(results.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const tooltipTitle = () => (!selectedRowKeys.length || !current?.recibo_de_ingreso ? (
    <Tooltip
      title="Esta pestaña solo es accesible si el depósito en garantía ya fue pagado."
    >
      Recibo
    </Tooltip>
  ) : (<span>Recibo</span>));

  const cancelDeposito = async () => {
    try {
      setLoading(true);
      await formCancel.validateFields();
      const [key] = selectedRowKeys;
      const values = formCancel.getFieldsValue();
      values.registro_de_beneficiario = registro_de_beneficiario;
      const canceled = await postCancelarDepositoEnGarantia(key, values);
      if (canceled) {
        const mappedData = data.map((e) => (e.id === canceled.id ? canceled : e));
        setData(mappedData);
        setVisibleCancel(false);
        await onClickEdit();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const controls = [
    (mutable
      && selectedRowKeys.length
      && permission?.change
      && current?.recibo_de_ingreso) && {
      text: 'Cancelar',
      icon: <CloseCircleOutlined />,
      onClick: () => setVisibleCancel(true),
    },
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    (mutable && permission?.add) && {
      text: 'Guardar',
      icon: <SaveOutlined />,
      onClick: () => onFinish(),
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {_entidad?.tipo_de_cargo ? (
          <>
            {!visible && (
              <CustomTable
                cols={columns}
                data={data}
                rowSelection={rowSelection}
                mobileColIndex={0}
                rowKey="id"
                controls={{
                  onClickAdd,
                  onClickEdit,
                }}
                customActions={[
                  {
                    text: 'Cancelar',
                    icon: <CloseCircleOutlined />,
                    onClick: () => setVisibleCancel(true),
                  },
                ]}
                allowDelete={false}
                handleOnRowClick={handleOnRowClick}
                permission={permission}
              />
            )}
            {visible && (
              <Card
                className="form-container"
                bordered={false}
                title={(
                  <FormSubmitCustomControls
                    label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Depósito`}
                    controls={controls}
                  />
                )}
              >
                <Tabs
                  onChange={setCurrentTabKey}
                  activeKey={currentTabKey}
                  defaultActiveKey="general"
                  type="card"
                >
                  <TabPane
                    key="general"
                    tab="Datos del Depósito"
                    forceRender
                  >
                    <Form
                      form={form}
                      layout="vertical"
                      name="general"
                      scrollToFirstError
                      onFinish={() => onFinish()}
                      initialValues={{ estatus_de_deposito: 1 }}
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="usuario_que_emitio"
                            label="usuario que emitió"
                            hasFeedback
                          >
                            <Select
                              dataSource={emisor}
                              disabled
                              labelProp="nombre_completo"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            label="Cantidad de Depósito"
                            name="importe_de_deposito"
                            rules={rules.importe}
                            decimal
                            prefix={moneda?.simbolo || '$'}
                            disabled={!mutable}
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estatus_de_deposito"
                            label="Estatus"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Select
                              dataSource={estatus}
                              disabled={!mutable || !selectedRowKeys.length}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={16}>
                          <Form.Item
                            name="observaciones_de_deposito"
                            label="Observaciones"
                            rules={rules.required}
                          >
                            <Input.TextArea
                              minLength={10}
                              autoSize={{ minRows: 4, maxRows: 4 }}
                              disabled={!mutable}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name="usuario_emitio"
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                  {permission.tabs.movimientodepositodegarantia && (
                    <TabPane
                      tab="Movimientos"
                      key="movimientos"
                      forceRender
                      disabled={!selectedRowKeys.length}
                    >
                      <MovimientosDeDepositos
                        depositoID={selectedRowKeys[0]}
                        permission={permission.tabs.movimientodepositodegarantia}
                      />
                    </TabPane>
                  )}
                  {permission.view_recibo && (
                    <TabPane
                      key="recibo"
                      tab={tooltipTitle()}
                      disabled={!selectedRowKeys.length || !current?.recibo_de_ingreso}
                    >
                      <Form
                        name="recibo"
                        form={formRecibo}
                        layout="vertical"
                        scrollToFirstError
                        onFinish={() => onFinish()}
                      >
                        <Row gutter={10}>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="usuario_que_emitio"
                              label="usuario que emitió"
                              hasFeedback
                            >
                              <Select
                                dataSource={emisor}
                                disabled
                                labelProp="nombre_completo"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="folio_de_recibo_de_ingreso"
                              label="Folio del Recibo"
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="fecha_emision_de_recibo"
                              label="Fecha de emisión"
                            >
                              <DatePicker format={DATE_FRONTEND_FORMAT} placeholder="" disabled />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Button
                              onClick={onClickPreview}
                              block
                              style={{ marginTop: 21 }}
                            >
                              <EyeOutlined />
                              <Typography.Text>
                                Ver Recibo
                              </Typography.Text>
                            </Button>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Button
                              onClick={onClickTicket}
                              block
                              style={{ marginTop: 21 }}
                            >
                              <EyeOutlined />
                              <Typography.Text>
                                Ver Ticket
                              </Typography.Text>
                            </Button>
                          </Col>
                        </Row>
                        <Form.Item hidden>
                          <Button htmlType="submit" />
                        </Form.Item>
                      </Form>
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            )}
          </>
        ) : (
          <Result
            title="Se requiere la configuración de un tipo de cargo para depósito de garantía"
            extra={permission.view_configuracionentidad ? (
              <Button onClick={() => history.push('/configuracion/entidad?tab=configurations')}>
                Ir a Configuración de la entidad
              </Button>
            ) : null}
          />
        )}
      </Spin>
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
        id={selectedRowKeys[0]}
        baseURL={baseURI}
        allowSend={false}
      />
      <Modal
        visible={visibleCancel}
        footer={null}
        title={(
          <FormSubmitControls
            label="Cancelar Depósito"
            onCancel={() => setVisibleCancel(false)}
            onFinish={() => cancelDeposito()}
          />
        )}
        closable={false}
        maskClosable={false}
      >
        <Row justify="center">
          <Col span={24}>
            <Form
              form={formCancel}
              layout="vertical"
              scrollToFirstError
              onFinish={() => cancelDeposito()}
            >
              <Col span={24}>
                <Form.Item
                  name="motivo_de_cancelacion"
                  label="Motivo de cancelación"
                  rules={rules.required}
                >
                  <Input.TextArea
                    minLength={10}
                    autoSize={{ minRows: 4, maxRows: 4 }}
                  />
                </Form.Item>
              </Col>
            </Form>
          </Col>
        </Row>
      </Modal>
    </Row>
  );
};

const Result = styled(AntResult)`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export default DepositosEnGarantia;
